import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const SafetyAlertContext = createContext(null);

export function SafetyAlertProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/sitemon/safety-alerts/ws`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'safety_alert' && data.event) {
            const event = data.event;
            const det = event.detection || {};
            const severity = det.severity || 'MEDIUM';
            const detType = (det.detectionType || 'UNKNOWN').replace(/_/g, ' ');
            const conf = ((det.confidence || 0) * 100).toFixed(0);

            setAlerts(prev => [{ ...event, receivedAt: new Date().toISOString() }, ...prev].slice(0, 50));
            setUnreadCount(prev => prev + 1);

            const toastFn = severity === 'CRITICAL' ? toast.error : severity === 'HIGH' ? toast.warning : toast.info;
            toastFn(
              `${severity}: ${detType} — ${event.cameraCode || 'Camera'} (${conf}% conf)`,
              {
                description: `${event.area || ''} — ${det.violatingPersonsCount || 0} violating of ${det.totalPersonsInFrame || 0} persons`,
                duration: severity === 'CRITICAL' ? 12000 : 6000,
              }
            );
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  const clearAlerts = () => { setAlerts([]); setUnreadCount(0); };
  const markRead = () => setUnreadCount(0);

  return (
    <SafetyAlertContext.Provider value={{ alerts, unreadCount, connected, clearAlerts, markRead }}>
      {children}
    </SafetyAlertContext.Provider>
  );
}

export const useSafetyAlerts = () => useContext(SafetyAlertContext);
